// Подсказка «?» — механизм `G21`, разгрузка поверхности `G44`.
//
// Всплывающее окно, а не подсказка при наведении: внутри ссылка, а ссылку в
// tooltip мышью не поймать и с клавиатуры не достать. Радиксовый `Popover`
// берёт на себя Esc, возврат фокуса на кнопку и связку `aria-*` — ровно то,
// ради чего `shadcn/ui` и стоит в паре с Tailwind.
//
// Подсказка по ситуации (слово владельца 30.09): кроме «що це · як читати ·
// сноска» — типовые блоки, которые экран включает там, где они помогают
// понять: формула с текущими числами, легенда значков и цветов, шкала с
// отметкой текущего, источник и время, действие. Знак один на систему; золотой
// заполненный (`key`) — один на экран, у главного числа или вопроса экрана.

import { Link, useRouter } from "@tanstack/react-router";
import { ArrowRight, Database, MapPin } from "lucide-react";
import { useId, type ReactNode } from "react";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { StatusIcon, toneText, type Tone } from "@/components/ui/status-icon";

import { capital, fill, fromDictionary } from "@/api/words";

import { assertHintFilled, type FieldHint, type Hint } from "./types";

// Слова рамки подсказки — словарь (`G117`, таск 74). До входа они тоже нужны:
// у экрана входа есть свои «?».
const words = fromDictionary(
  {
    what: "hint_title_what",
    read: "hint_title_read",
    about: "hint_about",
    formula: "hint_block_formula",
    legend: "hint_block_legend",
    scale: "hint_block_scale",
    now: "hint_scale_now",
    example: "hint_block_example",
    empty: "hint_block_empty",
  },
  { beforeLogin: true },
);

/**
 * Строка «знак · название · число» блока «як складається».
 *
 * ⚠⚠ `value` — строка, которую экран **уже показывает** рядом (та же функция
 * показа, то же число ручки). Подсказка не считает: ни суммы, ни разности, ни
 * округления. `null` — числа нет, и строка говорит это словом `missing`, а не
 * нулём (правило 10).
 */
export interface HintFormulaRow {
  readonly sign: "+" | "−";
  readonly name: string;
  readonly value: string | null;
  /** Слово вместо числа, когда его нет: «немає числа», «очікує». */
  readonly missing?: string;
}

export interface HintFormula {
  readonly rows: readonly HintFormulaRow[];
  /** Итог чертой — **готовое** число ручки, не сумма строк выше. Нет числа — нет итога. */
  readonly total: { readonly name: string; readonly value: string } | null;
}

/** Пункт легенды: значок или образец цвета (рисует экран) + слово + пояснение. */
export interface HintMark {
  readonly mark: ReactNode;
  readonly word: string;
  readonly text?: string;
}

/** Ступень шкалы: тон значка, слово, пояснение. */
export interface HintStep {
  readonly tone: Tone;
  readonly word: string;
  readonly text?: string;
}

export interface HintScale {
  readonly steps: readonly HintStep[];
  /** Номер текущей ступени; `null` — текущая вне шкалы (например, ещё не было ни одной). */
  readonly current: number | null;
  /** Порог словами — только если он есть в ответе сервера: «межа — 60 хв». */
  readonly note?: string;
}

/**
 * Добавочные блоки подсказки по ситуации и вид знака.
 *
 * ⚠ Всё — готовые строки экрана. Новых чисел подсказка не заводит.
 */
export interface HintMore {
  /**
   * Золотой заполненный «?» — **один на экран**, у главного числа или вопроса
   * экрана. Остальные — тихие. Проверяется тестом на каждом экране.
   */
  readonly key?: boolean;
  /** Пример значения: вдавленная плашка, числа `tabular-nums`. У поля ввода обязателен. */
  readonly example?: string;
  /** Что будет, если поле пусто: одна строка словами. У поля ввода обязателен. */
  readonly empty?: string;
  readonly formula?: HintFormula;
  readonly legend?: readonly HintMark[];
  readonly scale?: HintScale;
  /** Откуда и когда: «DnTrade · 30.09, 12:50», «підтверджено бухгалтером». */
  readonly source?: string;
  /**
   * Где это вносят или исправляют — если место человеку выдано. `to` есть —
   * переход; нет (экран ещё не построен) — адрес словами.
   */
  readonly action?: { readonly label: string; readonly to?: string };
}

interface HintButtonProps {
  /** Чему подсказка. Попадает в имя кнопки для экранного чтеца. */
  readonly about: string;
  readonly hint: Hint | FieldHint;
  /** Первая строка окна — текущее состояние словами (свежесть в шапке). */
  readonly lead?: string | undefined;
  /** Блоки по ситуации и вид знака. Нет — подсказка как была: «що це · як читати · сноска». */
  readonly more?: HintMore | undefined;
  /** Тихий знак без кольца (строка состояния). */
  readonly bare?: boolean;
}

const ACTION_CLASS =
  "relative inline-flex min-h-6 items-center gap-1 self-start rounded-control text-sm font-medium text-link hover:underline after:absolute after:-inset-y-2.5 after:-inset-x-1 after:content-['']";

/** Переход из подсказки. Без маршрутизатора (тест, витрина) — обычная ссылка: адрес тот же, вид тот же. */
function ActionLink({ to, label }: { readonly to: string; readonly label: string }) {
  const router = useRouter({ warn: false });
  const inside = (
    <>
      {label}
      <ArrowRight aria-hidden="true" className="size-4" />
    </>
  );
  return router ? (
    <Link to={to} className={ACTION_CLASS}>{inside}</Link>
  ) : (
    <a href={to} className={ACTION_CLASS}>{inside}</a>
  );
}

/** Подпись блока подсказки: мелкими капсами, как у «що це». */
function BlockTitle({ children }: { readonly children: ReactNode }) {
  return <dt className="text-2xs font-medium tracking-wide text-ink-2 uppercase">{children}</dt>;
}

/** «Приклад»: значение в вдавленной плашке — так его и вводят. */
function ExampleBlock({ example }: { readonly example: string }) {
  return (
    <div data-hint-block="example">
      <BlockTitle>{words.example}</BlockTitle>
      <dd className="mt-1">
        <span
          data-numbers
          className="inline-block max-w-full rounded-control border border-line bg-sunken px-sm py-1 text-sm break-words text-ink tabular-nums"
        >
          {example}
        </span>
      </dd>
    </div>
  );
}

/**
 * «Якщо порожньо»: одна строка словами — порожнє не стає нулём. Значка нет:
 * пунктирный круг «невідомо» в окне читался как загрузка; слово и заголовок
 * блока говорят то же. Цвет вторичный: первым читается «що це».
 */
function EmptyBlock({ empty }: { readonly empty: string }) {
  return (
    <div data-hint-block="empty">
      <BlockTitle>{words.empty}</BlockTitle>
      <dd className="mt-1 text-sm text-ink-2">{empty}</dd>
    </div>
  );
}

/** «На що впливає»: фразы через перевод строки, каждая — своя строка с маркером. */
function AffectsBlock({ text }: { readonly text: string }) {
  const lines = text.split("\n").map((one) => one.trim()).filter((one) => one !== "");
  return (
    <div data-hint-block="affects">
      <BlockTitle>{words.read}</BlockTitle>
      <dd>
        <ul className="mt-1 flex list-disc flex-col gap-1 pl-4 text-sm text-ink-2 marker:text-ink-3">
          {lines.map((one, at) => (
            <li key={`${at}:${one}`}>{one}</li>
          ))}
        </ul>
      </dd>
    </div>
  );
}

const FORMULA_ROW = "grid grid-cols-[0.75rem_minmax(0,1fr)_auto] items-baseline gap-x-sm";

/** «Як складається»: строки со знаком и итог чертой — числа экрана как есть. */
function FormulaBlock({ formula }: { readonly formula: HintFormula }) {
  return (
    <div>
      <BlockTitle>{words.formula}</BlockTitle>
      <dd>
        <ul data-hint-formula className="mt-1 flex flex-col text-sm">
          {formula.rows.map((row, at) => (
            <li key={`${row.name}:${at}`} data-hint-row className={`${FORMULA_ROW} py-0.5`}>
              <span className="font-mono text-ink-3">{row.sign}</span>
              <span className="text-ink-2">{row.name}</span>
              {row.value === null ? (
                <span className="text-right text-ink-3 italic">{row.missing ?? "—"}</span>
              ) : (
                <span data-numbers data-hint-value className="text-right whitespace-nowrap text-ink tabular-nums">
                  {row.value}
                </span>
              )}
            </li>
          ))}
          {formula.total === null ? null : (
            <li data-hint-total className={`${FORMULA_ROW} mt-1 border-t border-line pt-1.5`}>
              <span className="font-mono text-ink-3">=</span>
              <span className="font-semibold text-ink">{formula.total.name}</span>
              <span
                data-numbers
                data-hint-value
                className="text-right font-semibold whitespace-nowrap text-ink tabular-nums"
              >
                {formula.total.value}
              </span>
            </li>
          )}
        </ul>
      </dd>
    </div>
  );
}

/** «Позначки»: значок или образец цвета, жирное слово и пояснение — как в референсе. */
function LegendBlock({ legend }: { readonly legend: readonly HintMark[] }) {
  return (
    <div>
      <BlockTitle>{words.legend}</BlockTitle>
      <dd>
        <ul className="mt-1 flex flex-col gap-1.5 text-sm">
          {legend.map((one) => (
            <li key={one.word} className="grid grid-cols-[1rem_minmax(0,1fr)] items-start gap-x-sm">
              <span className="mt-0.5 flex size-4 items-center justify-center">{one.mark}</span>
              <span className="text-ink-2">
                <span className="font-semibold text-ink">{capital(one.word)}</span>
                {one.text === undefined || one.text === "" ? null : <> — {one.text}</>}
              </span>
            </li>
          ))}
        </ul>
      </dd>
    </div>
  );
}

/** «Шкала»: ступени от лучшей к худшей; текущая отмечена словом «зараз», не только цветом. */
function ScaleBlock({ scale }: { readonly scale: HintScale }) {
  return (
    <div>
      <BlockTitle>{words.scale}</BlockTitle>
      <dd>
        <ol className="mt-1 flex flex-col gap-0.5 text-sm">
          {scale.steps.map((step, at) => {
            const now = at === scale.current;
            return (
              <li
                key={step.word}
                data-hint-now={now ? "" : undefined}
                aria-current={now ? "step" : undefined}
                className={`grid grid-cols-[1rem_minmax(0,1fr)] items-start gap-x-sm rounded-control px-1.5 py-1 ${now ? "bg-overlay" : ""}`}
              >
                <StatusIcon tone={step.tone} muted={!now} className="mt-0.5 size-4" />
                <span className="text-ink-2">
                  <span className={`font-semibold ${now ? toneText(step.tone, "text-ink") : "text-ink"}`}>
                    {step.word}
                  </span>
                  {now ? (
                    <span className="ml-1.5 rounded-full border border-line px-1.5 text-2xs font-semibold tracking-wide text-ink uppercase">
                      {words.now}
                    </span>
                  ) : null}
                  {step.text === undefined || step.text === "" ? null : <span className="block">{step.text}</span>}
                </span>
              </li>
            );
          })}
        </ol>
        {scale.note === undefined || scale.note === "" ? null : (
          <p className="mt-1 px-1.5 text-xs text-ink-2">{scale.note}</p>
        )}
      </dd>
    </div>
  );
}

/** Вид знака: тихий — контур; `key` — золотой заполненный, один на экран. */
const TRIGGER = {
  quiet: "border border-line-control text-ink-3 hover:border-accent-line hover:text-accent-ink",
  key: "border border-accent-ink bg-accent-ink text-on-accent hover:ring-2 hover:ring-accent-line",
} as const;

/** «?» без кольца — в строке состояния, где ряд знаков вокруг шумел бы (бриф §6 от 30.09). */
const TRIGGER_BARE = "text-ink-3 hover:text-accent-ink";

export function HintButton({ about, hint, lead, more, bare = false }: HintButtonProps) {
  assertHintFilled(hint, about);
  const titleId = useId();
  // Поле ввода несёт пример и «якщо порожньо» в самой подсказке; `more` может задать своё.
  const example = more?.example ?? ("example" in hint ? hint.example : undefined);
  const empty = more?.empty ?? ("empty" in hint ? hint.empty : undefined);
  const variant = more?.key === true ? "key" : "quiet";
  // У поля ввода окно — сверху, а не снизу: снизу оно закрывало бы само поле.
  const fieldHint = example !== undefined || empty !== undefined;
  // Сноска — механика расчёта. У поля ввода она пересказывала бы «що це», поэтому
  // там её нет, кроме окон с формулой или порогами. У остальных «?» сноска как была.
  const showNote = !fieldHint || more?.formula !== undefined || more?.scale !== undefined;
  const rich =
    more !== undefined &&
    (more.formula !== undefined || (more.legend?.length ?? 0) > 0 || more.scale !== undefined);

  return (
    <Popover>
      <PopoverTrigger
        // ⚠ Видимый кружок остаётся 24px, а берётся мишень 44×44 — её даёт
        // невидимый псевдоэлемент вокруг. Раздувать сам кружок нельзя: он
        // стоит у каждого денежного числа и у каждой строки таблицы, и
        // 44-пиксельные кружки развалили бы плотность финансового экрана.
        // Соседние «?» рядом не стоят, так что расширенные мишени не
        // перекрываются.
        data-hint={variant}
        className={`relative inline-flex size-6 shrink-0 items-center justify-center rounded-full transition-colors after:absolute after:-inset-[11px] after:content-[''] ${bare ? TRIGGER_BARE : TRIGGER[variant]}`}
        // Имя кнопки — вопрос человека, а не знак «?»: чтец читает вслух
        // «кнопка знак вопроса» и не говорит, о чём она.
        aria-label={fill(words.about, { about })}
      >
        <span aria-hidden="true" className={`text-xs leading-none ${variant === "key" ? "font-bold" : "font-medium"}`}>
          ?
        </span>
      </PopoverTrigger>
      {/* Окно названо своим заголовком: чтец объявляет «о чём», а не «диалог».
          Ширина — под содержимое: с формулой, легендой или шкалой шире, но не
          за край экрана (`collisionPadding` держит 16px от края). */}
      <PopoverContent
        // Ритм: внутри блока — 4px (подпись → текст), между блоками — 12px,
        // между частями окна (заголовок, блоки, источник, сноска) — 16px.
        className={`${rich ? "w-[25rem]" : "w-80"} max-w-[calc(100vw-2rem)] gap-md border border-line`}
        align="start"
        side={fieldHint ? "top" : "bottom"}
        collisionPadding={16}
        aria-labelledby={titleId}
      >
        <p id={titleId} className="text-sm font-semibold text-ink">
          {about}
        </p>
        {lead === undefined || lead === "" ? null : <p className="text-sm text-ink">{lead}</p>}
        <dl className="flex flex-col gap-sm">
          {/* «Що це» — первая строка основным цветом, без подписи капсами;
              подпись остаётся для чтеца. */}
          <div data-hint-block="what">
            <dt className="sr-only">{words.what}</dt>
            <dd className="text-sm text-ink">{hint.what}</dd>
          </div>
          {example === undefined || example === "" ? null : <ExampleBlock example={example} />}
          {more?.formula === undefined ? null : <FormulaBlock formula={more.formula} />}
          {more?.legend === undefined || more.legend.length === 0 ? null : <LegendBlock legend={more.legend} />}
          {more?.scale === undefined ? null : <ScaleBlock scale={more.scale} />}
          <AffectsBlock text={hint.read} />
          {empty === undefined || empty === "" ? null : <EmptyBlock empty={empty} />}
        </dl>
        {more?.source === undefined || more.source === "" ? null : (
          <p className="flex items-center gap-1.5 text-xs text-ink-2">
            <Database aria-hidden="true" className="size-4 shrink-0 text-ink-3" />
            <span data-numbers>{more.source}</span>
          </p>
        )}
        {more?.action === undefined ? null : more.action.to === undefined ? (
          <p className="flex items-center gap-1.5 text-sm text-ink-2">
            <MapPin aria-hidden="true" className="size-4 shrink-0 text-ink-3" />
            {more.action.label}
          </p>
        ) : (
          <ActionLink to={more.action.to} label={more.action.label} />
        )}
        {/* Сноска — механика расчёта словами (`G117`): мельче и под чертой,
            чтобы «что это» и «как читать» читались первыми. Цвет — тот же, что
            у текста подсказки: пара проверена `tokens/pairs.ts`. */}
        {showNote ? <p className="border-t border-line pt-xs text-xs leading-normal text-ink-2">{hint.note}</p> : null}
        {/* ⚠⚠ Строки «Звідки правило: <код>» здесь БОЛЬШЕ НЕТ — снята правкой
            владельца 16.09. `G21` требует от подсказки ровно четыре вещи: что
            это · для чего · как работает · на что влияет; кода решения среди
            них нет, и на экране он человеку не нужен. Прослеживаемость от этого
            не теряется: поле `rule` остаётся обязательным в типе `Hint` и
            обязательным к заполнению — оно живёт в коде и в документах, а не на
            поверхности. Служебные коды (`S-NN`, `FRM-NN`, `CFG-NN`, `DEC-*`,
            коды состояний) на поверхность не выводятся никогда. */}
      </PopoverContent>
    </Popover>
  );
}
